import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Shield, HelpCircle } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { areaStyle } from "@/lib/categories";
import { cn } from "@/lib/utils";
import imageUrl from "@/lib/imageUrl";

export const JudgmentCard = ({ judgment: j, index = 0 }) => {
  const { lang } = useLanguage();
  const st = areaStyle(j.area_of_law);
  // Prefer infographic_image as the card display photo (restored feature).
  // Fall back to cover_image, then to the CSS gradient header.
  // All paths are served through the backend resize/WebP pipeline so the
  // original multi-MB raster is never sent — only a ~10-30 KB WebP at 640 px.
  const bgImg = (j.infographic_image || j.cover_image)
    ? imageUrl(j.infographic_image || j.cover_image, { w: 640 })
    : null;

  // Use descriptive plain title if available, fall back to case_title
  const displayTitle = lang === "hi"
    ? (j.plain_title_hi || j.case_title)
    : (j.plain_title_en || j.case_title);
  const caseRef = j.case_title !== displayTitle ? j.case_title : null;

  const question = lang === "hi" ? j.headline_question_hi : j.headline_question_en;

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, delay: Math.min(index * 0.07, 0.42) }}
      className="group relative overflow-hidden rounded-2xl border bg-card shadow-sm transition-shadow duration-200 hover:shadow-lg"
      data-testid="judgment-list-item"
    >
      <Link to={`/judgments/${j.slug || j.id}`} className="block">
        {/* Image / infographic background */}
        {bgImg ? (
          <div className="relative h-44 w-full overflow-hidden">
            <img
              src={bgImg}
              alt={displayTitle}
              loading={index < 3 ? "eager" : "lazy"}
              fetchPriority={index === 0 ? "high" : undefined}
              decoding="async"
              width={640}
              height={352}
              className="absolute inset-0 h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
            />
            {/* Gradient overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
            {/* Headline question overlay */}
            {question && (
              <div className="absolute bottom-0 left-0 right-0 p-3">
                <div className="flex items-start gap-2 rounded-xl bg-white/10 px-3 py-2 backdrop-blur-sm border border-white/20">
                  <HelpCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-300" />
                  <p className="line-clamp-2 text-xs font-medium leading-snug text-white">{question}</p>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className={cn("relative flex h-44 w-full items-end bg-gradient-to-br p-4", st.grad)}>
            <st.icon className="absolute right-4 top-4 h-16 w-16 text-white/20" />
            {question && (
              <div className="flex items-start gap-2 rounded-xl bg-white/10 px-3 py-2 backdrop-blur-sm border border-white/20 w-full">
                <HelpCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-200" />
                <p className="line-clamp-2 text-xs font-medium leading-snug text-white">{question}</p>
              </div>
            )}
          </div>
        )}

        {/* Card body */}
        <div className="p-4">
          {/* Plain descriptive title */}
          <h3 className="font-serif text-base font-semibold leading-snug text-foreground line-clamp-2">{displayTitle}</h3>
          {/* Case reference (formal case name, if different) */}
          {caseRef && (
            <p className="mt-0.5 text-xs text-muted-foreground line-clamp-1 italic">{caseRef}</p>
          )}

          {/* Category chip */}
          <div className="mt-2 flex flex-wrap gap-1">
            {j.area_of_law && (
              <span className={cn("inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium", st.chip)}>
                <st.icon className="mr-1 h-3 w-3" /> {j.area_of_law}
              </span>
            )}
            {j.verified && (
              <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-700">
                <Shield className="h-3 w-3" /> Verified
              </span>
            )}
          </div>

          {/* One-line summary */}
          {(j.one_line_en || j.one_line_hi) && (
            <p className="mt-2 line-clamp-2 text-sm text-muted-foreground">
              {lang === "hi" ? j.one_line_hi : j.one_line_en}
            </p>
          )}
        </div>
      </Link>
    </motion.div>
  );
};
